import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileMedical,
  faPen,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { Modal, Button, Row, Col, Form } from "react-bootstrap";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import FalconCloseButton from "components/common/FalconCloseButton";
import { Controller, useForm } from "react-hook-form";
import MultiSelect from "components/common/MultiSelect";
import { useDispatch, useSelector } from "react-redux";
import BootCheckbox from "components/common/custom/forms/BootCheckbox";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import FalconComponentCard from "components/common/FalconComponentCard";
import MedicalHistoryMedicine from "./MedicalHistoryMedicine";
import { getToday, ShowError } from "helpers/utils";
import ServiceCodeSelect from "./ServiceCodeSelect";
import { useEffect, useRef, useState } from "react";
import WizardModal from "components/wizard/WizardModal";
import {
  GetAllBodySite,
  GetAllClaimPreAuthAllList,
  GetAllRoute,
} from "../config/preauthorizationApi";
import {
  addValidationError,
  handleAddPreauthorizationServices,
  resetSelectedService,
  setHistoryModal,
  setViewModal,
} from "../config/preauthorizationSlice";
import { FaTooth } from "react-icons/fa";
import BootIconButton from "components/common/custom/IconButton";
import TeethModalSelect from "./TeethModalSelect";
import { useSearchParams } from "react-router-dom";

const MedicalHistoryModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const {
    serviceTypeList,
    bodySite,
    bodySubSite,
    selectedToothInfo,
    preauthorizationDiagnosis,
    doseList,
    validationError,
    viewModal,
    selectedService,
    stepFormData,
    servicesList,
    labTestList,
    practitionerListAdded,
    routeList,
    practitionerList,
    historyModalOpen,
    oralDentalList,
  } = useSelector((state) => state.preauthorization);
  const { singleEligibilityData } = useSelector(
    (state) => state.eligibilityCheckList
  );

  const [searchParams] = useSearchParams();

  const coverageEligibility =
    singleEligibilityData?.eligibility_response?.entry?.find(
      (item) => item?.resource?.resourceType === "Coverage"
    )?.resource;

  const beneficiaryCost = coverageEligibility?.costToBeneficiary;
  const eligibility = searchParams.get("eligibility") === "true";

  const { settingData } = useSelector((state) => state.settings);

  const isDental =
    stepFormData?.preauthorization_type?.claim_types_name === "Dental";

  const [showServiceCodeModal, setShowServiceCodeModal] = useState(false);
  const [menuStates, setMenuStates] = useState(false);
  const [teethModal, setTeethModal] = useState(false);
  const userModifiedPatientShare = useRef(false);

  const {
    watch,
    control,
    setValue,
    handleSubmit,
    reset,
    setError,
    formState: { errors, dirtyFields },
  } = useForm({
    defaultValues: {
      service_start_date: getToday(),
      service_end_date: getToday(),
      unit_price: "",
      discount: "",
      quantity: "",
      vat: "",
      net_amount: "",
      patient_share: "",
    },
  });

  const { unit_price, discount, quantity, vat, patient_share } = watch();

  const serviceCodeDesc = watch("service_code_description");

  const handleServiceMenuToggle = (isOpen) => {
    if (isOpen && !watch("service_type")) {
      setShowServiceCodeModal(true);
      return setMenuStates(isOpen);
    }
    setMenuStates((prev) => ({ ...prev, type: isOpen }));
  };

  useEffect(() => {
    if (patient_share === "" || parseFloat(patient_share) === 0) {
      userModifiedPatientShare.current = false;
    } else {
      userModifiedPatientShare.current = true;
    }
  }, [patient_share]);

  // reset flag when inputs change → re-enable system calculation
  useEffect(() => {
    userModifiedPatientShare.current = false;
  }, [unit_price, quantity, discount, vat]);

  // calculate amounts
  const watchedPatientShare = watch("patient_share");

  console.log({ watchedPatientShare })
  // calculate amounts
  useEffect(() => {
    const calculateAmounts = () => {
      const unitPriceVal = Number(unit_price) || 0;
      const discountVal = Number(discount) || 0;
      const quantityVal = Number(quantity) || 1;
      const vatVal = Number(vat) || 0;
      const formPatientShare = Number(watchedPatientShare) || 0;

      const total = unitPriceVal * quantityVal;
      const discountAmount = total * (discountVal / 100);
      const baseAmountAfterDiscount = Math.max(0, total - discountAmount);

      let calculatedPatientShare = 0;

      const safeFind = (code) =>
        beneficiaryCost?.find((item) => item?.type?.coding?.[0]?.code === code);

      // Determine maxPatientShare based on eligibility
      let maxPatientShare = baseAmountAfterDiscount;
      if (singleEligibilityData?.eligibilityid || eligibility) {
        const maxCopayRaw = singleEligibilityData?.coverage_info?.maxlimit;
        const copayPctRaw = singleEligibilityData?.coverage_info?.deductable;

        const maxCopay = Number(maxCopayRaw) || Infinity;
        const copayPct = Number(copayPctRaw) || 0;

        const totalExistingPatientShare = servicesList
          .filter((s) => s.id !== selectedService?.id)
          .reduce((sum, s) => sum + (Number(s.patient_share) || 0), 0);

        const remainingMaxCopay = Math.max(
          0,
          maxCopay - totalExistingPatientShare
        );

        maxPatientShare = Math.min(baseAmountAfterDiscount, remainingMaxCopay);

        const systemShare = Math.min(
          baseAmountAfterDiscount * (copayPct / 100),
          maxPatientShare
        );

        // If the field is not dirty, use systemShare, else respect user input
        if (!dirtyFields.patient_share) {
          calculatedPatientShare = Math.max(0, systemShare);
          if (Math.abs(formPatientShare - calculatedPatientShare) > 0.005) {
            setValue("patient_share", calculatedPatientShare.toFixed(2), {
              shouldValidate: true,
            });
          }
        } else {
          // field is dirty → user entered value, clamp 0-maxPatientShare
          calculatedPatientShare = Math.max(
            0,
            Math.min(formPatientShare, baseAmountAfterDiscount)
          );
          if (formPatientShare > baseAmountAfterDiscount + 0.005) {
            setValue("patient_share", baseAmountAfterDiscount.toFixed(2), {
              shouldValidate: true,
            });
          }
        }
      } else {
        // No eligibility → allow 0, clamp at baseAmountAfterDiscount
        const maxNoElig = baseAmountAfterDiscount;

        calculatedPatientShare = Math.max(
          0,
          Math.min(formPatientShare, maxNoElig)
        );
        if (formPatientShare > maxNoElig + 0.005) {
          setValue("patient_share", maxNoElig.toFixed(2), {
            shouldValidate: true,
          });
        }

      }

      // final clamp for safety
      calculatedPatientShare = Math.min(
        calculatedPatientShare,
        baseAmountAfterDiscount
      );
      calculatedPatientShare = Math.max(0, calculatedPatientShare);

      // compute net
      const baseAmount = baseAmountAfterDiscount - calculatedPatientShare;
      const calculatedNetAmount = (baseAmount * (1 + vatVal / 100)).toFixed(2);

      setValue("net_amount", calculatedNetAmount, { shouldValidate: true });
    };

    if ((dirtyFields?.quantity || dirtyFields?.discount || dirtyFields?.vat || dirtyFields?.unit_price || dirtyFields?.net_amount || dirtyFields?.patient_share)) {
      calculateAmounts();
    } else {
      calculateAmounts()
    }
  }, [
    unit_price,
    quantity,
    discount,
    vat,
    watchedPatientShare,
    singleEligibilityData,
    historyModalOpen,
    servicesList,
    selectedService,
    selectedService?.id,
    setValue,
    beneficiaryCost,
    eligibility,
    dirtyFields.patient_share, // important to track dirty state
  ]);

  // Modify the serviceCodeDesc effect to preserve patient share
  useEffect(() => {
    if (serviceCodeDesc && !selectedService?.id) {
      setValue("unit_price", serviceCodeDesc?.unit_price || "");
      setValue("discount", serviceCodeDesc?.price_discount || 0);
      setValue("vat", serviceCodeDesc?.price_tax || "");
      setValue("strength", serviceCodeDesc?.prodserv_strngt || "");
      setValue("quantity", 1);
      // Don't reset patient_share here
    }
  }, [serviceCodeDesc, selectedService?.id, setValue]);

  console.log({ patientShare: watch("patient_share") });

  useEffect(() => {
    if (!serviceCodeDesc) {
      setValue("unit_price", "");
      setValue("discount", "");
      setValue("vat", "");
      setValue("net_amount", "");
      setValue("quantity", 1);
      setValue("patient_share", 0); // Ensure patient_share resets to empty string
    }
  }, [serviceCodeDesc, setValue]);

  useEffect(() => {
    if (
      historyModalOpen &&
      (bodySite?.length === 0 || oralDentalList?.length === 0)
    ) {
      dispatch(GetAllClaimPreAuthAllList());
      dispatch(GetAllBodySite());
    }
  }, [historyModalOpen, bodySite, dispatch]);

  useEffect(() => {
    if (selectedToothInfo) {
      setValue(
        "oral_origin",
        oralDentalList?.find(
          (item) => item?.oral_code == selectedToothInfo?.number
        )
      );
    }
  }, [selectedToothInfo, oralDentalList, setValue, isDental]);

  useEffect(() => {
    if (routeList?.length === 0) {
      dispatch(GetAllRoute());
    }
  }, [routeList]);

  const addServices = (data) => {
    let hasError = false;

    console.log({ data });
    // return;

    if (data.service_start_date && data.service_end_date) {
      const startDate = new Date(data.service_start_date);
      const endDate = new Date(data.service_end_date);

      if (startDate > endDate) {
        setError("service_start_date", {
          type: "manual",
          message: "Start date cannot be after end date",
        });
        setError("service_end_date", {
          type: "manual",
          message: "End date cannot be before start date",
        });
        hasError = true;
      }
    }

    if (data?.unit_price < 1) {
      setError("unit_price", {
        type: "validate",
        message: "Unit Price can't be 0 or negative",
      });
      hasError = true;
    }

    if (data?.quantity < 1) {
      setError("quantity", {
        type: "validate",
        message: "Quantity can't be 0 or negative",
      });
      hasError = true;
    }

    // if (data?.service_type?.servtype_code === "itm_medcde" && !data?.strength) {
    //   setError("strength", {
    //     type: "validate",
    //     message: "Strength is required when service type is medical code ",
    //   });
    //   hasError = true;
    // }
    // if (
    //   data?.service_type?.servtype_code === "itm_hrblcde" &&
    //   !data?.strength
    // ) {
    //   setError("strength", {
    //     type: "validate",
    //     message: "Strength is required when service type is Herbal ",
    //   });
    //   hasError = true;
    // }

    if (hasError || validationError) return;

    if (selectedService?.id) {
      dispatch(
        handleAddPreauthorizationServices({
          type: "EDIT_SERVICES",
          payload: {
            id: selectedService.id,
            ...data,
            strength: "200 MG",
          },
        })
      );
    } else {
      dispatch(
        handleAddPreauthorizationServices({
          type: "ADD_SERVICES",
          payload: data,
        })
      );
    }

    onHide();
    reset();
  };

  console.log({ selectedService })

  useEffect(() => {
    if (selectedService?.id) {
      console.log({ sss: selectedService?.service_types });
      setValue("service_start_date", selectedService?.service_start_date);
      setValue("service_end_date", selectedService?.service_end_date);
      setValue("service_type", selectedService?.service_type);
      setValue(
        "service_code_description",
        selectedService?.service_code_description
      );
      setValue("maternity", selectedService?.maternity);
      setValue("unit_price", selectedService?.unit_price);
      setValue("discount", selectedService?.discount);
      setValue("quantity", selectedService?.quantity);
      setValue("vat", selectedService?.vat);
      setValue("net_amount", selectedService?.net_amount);
      setValue("patient_share", selectedService?.patient_share || ""); // Default to empty string if not present
      setValue("day_supply", selectedService?.day_supply);
      setValue("strength", selectedService?.strength);
      setValue("body_site", selectedService?.body_site);
      setValue("sub_site", selectedService?.sub_site);
      setValue("scientific_code", selectedService?.scientific_code);
      setValue("scientic_code_absence", selectedService?.scientic_code_absence);
      setValue("selection_reason", selectedService?.selection_reason);
      setValue("pharmacist_substitute", selectedService?.pharmacist_substitute);
      setValue(
        "prescribed_medication_code",
        selectedService?.prescribed_medication_code || ""
      );
      setValue(
        "prescribed_medication_type",
        selectedService?.prescribed_medication_type
      );
      setValue("start_date", selectedService?.start_date);
      setValue("end_date", selectedService?.end_date);
      setValue(
        "scientific_code_other_reason",
        selectedService?.scientific_code_other_reason
      );
      setValue(
        "substitute_reason_other",
        selectedService?.substitute_reason_other
      );
      setValue("duration", selectedService?.duration);
      setValue(
        "durationType",
        selectedService?.durationType || selectedService?.duration_type
      );
      setValue("frequency", selectedService?.frequency);
      setValue("period", selectedService?.period);
      setValue("period_info", selectedService?.period_info);
      setValue(
        "frequencyType",
        selectedService?.period_type || selectedService?.frequencyType
      );
      setValue("diagnosis", selectedService?.diagnosis);
      setValue("tooth_surface", selectedService?.tooth_surface);
      setValue("oral_origin", selectedService?.oral_origin);
      setValue("dose", selectedService?.dose);
      setValue(
        "doseType",
        selectedService?.dose_type || selectedService?.doseType
      );
      setValue("refill_count", selectedService?.refill_count);
      setValue("route", selectedService?.route);
      setValue("service_practitioner", selectedService?.service_practitioner);
      setValue("time_instruction", selectedService?.time_instruction);
      setValue(
        "lab_tests",
        selectedService?.lab_tests?.map(item => item.lab_test_code) || []
      );

      setValue("dosage_instruction", selectedService?.dosage_instruction);
      setValue("patient_instruction", selectedService?.patient_instruction);
      setValue(
        "additional_supporting_info",
        selectedService?.additional_supporting_info
      );

      // Force set userModifiedPatientShare to true if patient_share > 0 to preserve edited value
      if (parseFloat(selectedService?.patient_share) > 0) {
        userModifiedPatientShare.current = true;
      }
    } else if (settingData?.length > 0) {
      const data = settingData?.[0];
      setValue(
        "service_type",
        watch("service_type") ||
        serviceTypeList?.find(
          (item) => item?.servtype_code === data?.fieldsetting_txt?.prd_type
        )
      );
    } else {
      reset({
        service_end_date: getToday(),
        service_start_date: getToday(),
        service_type: null,
        service_code_description: null,
        maternity: false,
        unit_price: "",
        discount: "",
        quantity: "",
        vat: "",
        net_amount: "",
        patient_share: "",
        day_supply: "",
        strength: "",
        body_site: null,
        sub_site: null,
        scientific_code: "",
        scientic_code_absence: "",
        selection_reason: "",
        pharmacist_substitute: "",
        start_date: null,
        end_date: null,
        duration: "",
        scientific_code_other_reason: "",
        substitute_reason_other: "",
        duration_type: "",
        frequency: "",
        period: "",
        period_type: "",
        tooth_surface: null,
        oral_origin: null,
        dose_type: "",
        refill_count: "",
        route: "",
        time_instruction: "",
        dosage_instruction: "",
        patient_instruction: "",
        additional_supporting_info: "",
      });
    }
  }, [
    selectedService,
    historyModalOpen,
    reset,
    routeList,
    setValue,
    selectedService?.dose_type,
    doseList,
    selectedService?.period_type,
    selectedService?.dose_type,
    settingData,
    serviceTypeList,
    watch,
  ]);

  useEffect(() => {
    if (show && !selectedService?.id) {
      // Set principal diagnosis by default
      const principalDiag = preauthorizationDiagnosis?.find(
        (item) => item.type === "principal"
      );
      if (principalDiag) {
        setValue("diagnosis", [
          { code: principalDiag.code, description: principalDiag.description },
        ]);
      }

      // Set first practitioner from practitionerListAdded by default
      if (practitionerListAdded?.length > 0) {
        const firstItem = practitionerListAdded?.find(
          (item) => item?.role?.careroles_code?.toLowerCase() === "primary"
        );
        setValue("service_practitioner", {
          value: firstItem.practitioner.value,
          label: firstItem.practitioner.label,
        });
      }
    }
  }, [
    show,
    selectedService,
    selectedService?.dose,
    selectedService?.frequency,
    selectedService?.duration,
    preauthorizationDiagnosis,
    practitionerListAdded,
    setValue,
  ]);

  return (
    <>
      <Modal
        className="sticky"
        show={show}
        fullscreen={false}
        dialogClassName="fullscreen-modal "
      >
        <Form onSubmit={handleSubmit((data) => addServices(data))}>
          <Modal.Header className="d-flex  sticky-top justify-content-between align-items-center ps-4 pe-4 pt-3">
            <Modal.Title as="h5" className="text-600 fs-11">
              <FontAwesomeIcon icon={faFileMedical} className="me-2" />
              {viewModal ? "View" : selectedService?.id ? "Edit" : "Add"}{" "}
              Service
            </Modal.Title>
            <FalconCloseButton
              size="sm"
              onClick={() => {
                reset({
                  service_end_date: getToday(),
                  service_start_date: getToday(),
                  service_type: null,
                  service_code_description: null,
                  maternity: false,
                  unit_price: "",
                  discount: "",
                  quantity: "",
                  vat: "",
                  net_amount: "",
                  patient_share: "",
                  day_supply: "",
                  strength: "",
                  body_site: null,
                  sub_site: null,
                  scientific_code: "",
                  scientic_code_absence: "",
                  selection_reason: "",
                  pharmacist_substitute: "",
                  start_date: null,
                  end_date: null,
                  duration: "",
                  scientific_code_other_reason: "",
                  substitute_reason_other: "",
                  duration_type: "",
                  frequency: "",
                  period: "",
                  period_type: "",
                  tooth_surface: null,
                  oral_origin: null,
                  dose_type: "",
                  refill_count: "",
                  route: "",
                  time_instruction: "",
                  dosage_instruction: "",
                  patient_instruction: "",
                  additional_supporting_info: "",
                });
                dispatch(resetSelectedService());
                onHide();
                dispatch(setViewModal(false));
              }}
              className="position-relative"
            />
          </Modal.Header>
          <Modal.Body className="p-3">
            <Row className="g-3">
              <Col xs={12} sm={12} xl={6}>
                <Row>
                  <Col xs={12} xl={4}>
                    <BootDatePicker
                      name="service_start_date"
                      label="Start Date"
                      size="sm"
                      control={control}
                      rules={{ required: true }}
                      selected={watch("service_start_date")}
                      isClearable={true}
                      onChange={(date) => setValue("service_start_date", date)}
                      placeholder="dd/mm/yyyy"
                      dateFormat="dd-mm-yyyy"
                    />
                  </Col>
                  <Col xs={12} xl={4}>
                    <BootDatePicker
                      name="service_end_date"
                      label="End Date"
                      size="sm"
                      control={control}
                      rules={{ required: true }}
                      selected={watch("service_end_date")}
                      isClearable={true}
                      onChange={(date) => setValue("service_end_date", date)}
                      placeholder="dd/mm/yyyy"
                      dateFormat="dd-mm-yyyy"
                    />
                  </Col>
                  <Col xs={12} xl={4}>
                    <label className="form-label">
                      Service Type <span className="text-danger">*</span>
                    </label>
                    <Controller
                      name="service_type"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MultiSelect
                          {...field}
                          menuPlacement="auto"
                          closeMenuOnSelect={false}
                          isMulti={false}
                          options={serviceTypeList.map((item) => ({
                            value: item.servtype_code,
                            label: item.servtype_name,
                          }))}
                          disabled={viewModal}
                          value={
                            field.value
                              ? {
                                value: field.value.servtype_code,
                                label: field.value.servtype_name,
                              }
                              : null
                          }
                          onChange={(selected) => {
                            setValue(
                              "service_type",
                              selected
                                ? {
                                  servtype_code: selected.value,
                                  servtype_name: selected.label,
                                }
                                : null
                            );
                          }}
                          formatOptionLabel={({ label }) => <div>{label}</div>}
                          getOptionValue={(option) => option.value}
                          placeholder="Choose ..."
                        />
                      )}
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={12} sm={12} xl={6}>
                <Row>
                  <Col xs={10}>
                    <ServiceCodeSelect
                      control={control}
                      label="Service Code Description"
                      name="service_code_description"
                      selectedServiceType={watch("service_type")?.servtype_code}
                      setValue={setValue}
                      isRequired={true}
                      watch={watch}
                      handleServiceMenuToggle={handleServiceMenuToggle}
                    />
                  </Col>
                  <Col xs={2}>
                    <BootCheckbox
                      name="maternity"
                      label="Maternity"
                      control={control}
                      disabled={viewModal}
                      size="lg"
                    />
                  </Col>
                </Row>
              </Col>
              <Col xs={12} md={6}>
                <FalconComponentCard className="custom-card">
                  <FalconComponentCard.Body className="custom-body">
                    <Row>
                      <Col xs={6} md={2}>
                        <TextInput
                          name="unit_price"
                          label="Unit Price"
                          control={control}
                          size="sm"
                          type="number"
                          disabled={viewModal}
                          min={0}
                          rules={{ required: true, min: 0.01 }}
                          required={true}
                        />
                      </Col>
                      <Col xs={6} md={2}>
                        <TextInput
                          name="discount"
                          label="Discount"
                          control={control}
                          size="sm"
                          type="number"
                          disabled={viewModal}
                          min={0}
                          rules={{ required: true, min: 0 }}
                          required={true}
                        />
                      </Col>
                      <Col xs={6} md={2}>
                        <TextInput
                          name="quantity"
                          label="Quantity"
                          control={control}
                          size="sm"
                          type="number"
                          min={1}
                          disabled={viewModal}
                          rules={{ required: true, min: 1 }}
                          required={true}
                        />
                      </Col>
                      <Col xs={6} md={2}>
                        <TextInput
                          name="patient_share"
                          label="Patient Share"
                          control={control}
                          size="sm"
                          disabled={viewModal}
                          type="number"
                          min={0}
                          required={false}
                        />
                      </Col>
                      <Col xs={6} md={2}>
                        <TextInput
                          name="net_amount"
                          label="Net Amount"
                          control={control}
                          size="sm"
                          disabled
                          rules={{ required: true }}
                          required={true}
                        />
                      </Col>
                      <Col xs={6} md={2}>
                        <Controller
                          name="vat"
                          control={control}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <Form.Group>
                              <Form.Label className="form-label">
                                VAT %
                              </Form.Label>
                              <Form.Select
                                {...field}
                                size="sm"
                                disabled={viewModal}
                                onChange={(e) =>
                                  field.onChange(Number(e.target.value))
                                }
                              >
                                <option value={0}>0</option>
                                <option value={15}>15</option>
                              </Form.Select>
                            </Form.Group>
                          )}
                        />
                      </Col>
                      {watch("service_type")?.servtype_code ===
                        "itm_medcde" && (
                          <Col xs={6} md={2}>
                            <TextInput
                              name="day_supply"
                              label="Days Supply"
                              control={control}
                              disabled={viewModal}
                              size="sm"
                              type="number"
                              min={0}
                              rules={{
                                required:
                                  watch("service_type")?.servtype_code ===
                                  "itm_medcde",
                              }}
                              required={
                                watch("service_type")?.servtype_code ===
                                "itm_medcde"
                              }
                            />
                          </Col>
                        )}
                    </Row>
                  </FalconComponentCard.Body>
                </FalconComponentCard>
              </Col>
              <Col xs={12} md={6}>
                <FalconComponentCard className="custom-card">
                  <FalconComponentCard.Body className="custom-body">
                    <Row className="d-flex align-items-center">
                      {!isDental && (
                        <Col xs={12} md={4}>
                          <label className="form-label">Body Site</label>
                          <Controller
                            name="body_site"
                            control={control}
                            rules={{ required: false }}
                            render={({ field }) => (
                              <MultiSelect
                                {...field}
                                menuPlacement="auto"
                                closeMenuOnSelect={false}
                                isMulti={false}
                                isClearable={true}
                                disabled={viewModal}
                                options={bodySite?.map((item) => ({
                                  value: item.bodysite_code,
                                  label: item.bodysite_display,
                                }))}
                                value={
                                  field.value
                                    ? {
                                      value: field.value.bodysite_code,
                                      label: field.value.bodysite_display,
                                    }
                                    : null
                                }
                                onChange={(selected) => {
                                  setValue(
                                    "body_site",
                                    selected
                                      ? {
                                        bodysite_code: selected.value,
                                        bodysite_display: selected.label,
                                      }
                                      : null
                                  );
                                }}
                                formatOptionLabel={({ label }) => (
                                  <div>{label}</div>
                                )}
                                getOptionValue={(option) => option.value}
                                placeholder="Choose ..."
                              />
                            )}
                          />
                        </Col>
                      )}
                      {!isDental && (
                        <Col xs={12} md={4}>
                          <label className="form-label">Sub Site</label>
                          <Controller
                            name="sub_site"
                            control={control}
                            rules={{ required: false }}
                            render={({ field }) => (
                              <MultiSelect
                                {...field}
                                menuPlacement="auto"
                                closeMenuOnSelect={false}
                                disabled={viewModal}
                                isMulti={false}
                                isClearable={true}
                                options={bodySubSite?.map((item) => ({
                                  value: item.bodysubsite_code,
                                  label: item.bodysubsite_display,
                                }))}
                                value={
                                  field.value
                                    ? {
                                      value: field.value.bodysubsite_code,
                                      label: field.value.bodysubsite_display,
                                    }
                                    : null
                                }
                                onChange={(selected) => {
                                  setValue(
                                    "sub_site",
                                    selected
                                      ? {
                                        bodysubsite_code: selected.value,
                                        bodysubsite_display: selected.label,
                                      }
                                      : null
                                  );
                                }}
                                formatOptionLabel={({ label }) => (
                                  <div>{label}</div>
                                )}
                                getOptionValue={(option) => option.value}
                                placeholder="Choose ..."
                              />
                            )}
                          />
                        </Col>
                      )}
                      {isDental && (
                        <Col xs={12} md={4}>
                          <Row>
                            <Col xs={viewModal ? 12 : 9}>
                              <label className="form-label">Oral Region</label>
                              <Controller
                                name="oral_origin"
                                control={control}
                                rules={{ required: false }}
                                render={({ field }) => (
                                  <MultiSelect
                                    {...field}
                                    menuPlacement="auto"
                                    closeMenuOnSelect={false}
                                    isMulti={false}
                                    isClearable={true}
                                    disabled={viewModal}
                                    options={oralDentalList?.map((item) => ({
                                      value: item.oral_code,
                                      label: item.oralc_description,
                                    }))}
                                    value={
                                      field.value
                                        ? {
                                          value: field.value.oral_code,
                                          label:
                                            field.value.oralc_description,
                                        }
                                        : null
                                    }
                                    onChange={(selected) => {
                                      setValue(
                                        "oral_origin",
                                        selected
                                          ? {
                                            oral_code: selected.value,
                                            oralc_description: selected.label,
                                          }
                                          : null
                                      );
                                    }}
                                    formatOptionLabel={({ label }) => (
                                      <div>{label}</div>
                                    )}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => option.value}
                                    onMenuOpen={() => {
                                      setTimeout(() => {
                                        const selectedEl =
                                          document.querySelector(
                                            ".react-select__option--is-selected"
                                          );
                                        if (selectedEl) {
                                          selectedEl.scrollIntoView({
                                            block: "center",
                                            behavior: "smooth",
                                          });
                                        }
                                      }, 100);
                                    }}
                                    classNamePrefix="react-select"
                                    placeholder="Choose ..."
                                  />
                                )}
                              />
                            </Col>
                            {!viewModal && (
                              <Col xs={3}>
                                <BootIconButton
                                  onClick={() => {
                                    setTeethModal(true);
                                  }}
                                  size="small"
                                  variant="danger"
                                  className="mt-4"
                                >
                                  <FaTooth />
                                </BootIconButton>
                              </Col>
                            )}
                          </Row>
                        </Col>
                      )}
                      {isDental && (
                        <Col xs={12} md={4}>
                          <label className="form-label">Tooth Surface</label>
                          <Controller
                            name="tooth_surface"
                            control={control}
                            rules={{ required: false }}
                            render={({ field }) => (
                              <MultiSelect
                                {...field}
                                menuPlacement="auto"
                                closeMenuOnSelect={false}
                                isMulti={false}
                                isClearable={true}
                                disabled={viewModal}
                                options={bodySubSite?.map((item) => ({
                                  value: item.bodysubsite_code,
                                  label: item.bodysubsite_display,
                                }))}
                                value={
                                  field.value
                                    ? {
                                      value: field.value.bodysubsite_code,
                                      label: field.value.bodysubsite_display,
                                    }
                                    : null
                                }
                                onChange={(selected) => {
                                  setValue(
                                    "tooth_surface",
                                    selected
                                      ? {
                                        bodysubsite_code: selected.value,
                                        bodysubsite_display: selected.label,
                                      }
                                      : null
                                  );
                                }}
                                formatOptionLabel={({ label }) => (
                                  <div>{label}</div>
                                )}
                                getOptionValue={(option) => option.value}
                                placeholder="Choose ..."
                              />
                            )}
                          />
                        </Col>
                      )}
                      <Col xs={12} md={4}>
                        <label className="form-label">
                          Diagnosis{" "}
                          {watch("service_type")?.servtype_code ===
                            "itm_medcde" && (
                              <span className="text-danger">*</span>
                            )}
                        </label>
                        <Controller
                          name="diagnosis"
                          control={control}
                          rules={{
                            validate: (value) =>
                              watch("service_type")?.servtype_code ===
                                "itm_medcde"
                                ? (Array.isArray(value) && value.length > 0) ||
                                "At least one diagnosis is required"
                                : true,
                          }}
                          render={({ field }) => (
                            <MultiSelect
                              {...field}
                              menuPlacement="auto"
                              closeMenuOnSelect={false}
                              isClearable={true}
                              disabled={viewModal}
                              isMulti={true}
                              value={
                                preauthorizationDiagnosis
                                  ?.filter((d) =>
                                    (field.value || []).some(
                                      (fv) => fv?.code === d?.code
                                    )
                                  )
                                  .map((d) => ({
                                    label: d.description,
                                    code: d.code,
                                    value: d.code,
                                  })) || []
                              }
                              onChange={(selectedOptions) => {
                                const newValue = selectedOptions.map(
                                  (option) => ({
                                    code: option.code,
                                    description: option.label,
                                  })
                                );
                                setValue("diagnosis", newValue);
                              }}
                              formatOptionLabel={({ label, code }) => (
                                <div>
                                  ({code}) {label}
                                </div>
                              )}
                              getOptionValue={(option) => option.code}
                              options={preauthorizationDiagnosis?.map(
                                (item) => ({
                                  label: item.description,
                                  code: item.code,
                                  value: item.code,
                                  type: item.type,
                                })
                              )}
                              placeholder="Choose ..."
                            />
                          )}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Group controlId="service_practitioner">
                          <label className="form-label">Practitioner</label>
                          <Controller
                            name="service_practitioner"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <MultiSelect
                                {...field}
                                isMulti={false}
                                menuPlacement="auto"
                                disabled={viewModal}
                                options={practitionerListAdded?.map((item) => ({
                                  value: item.practitioner.value,
                                  label: item.practitioner.label,
                                }))}
                                placeholder="Select Practitioner"
                                isClearable={true}
                                value={field.value}
                                onChange={(selected) => {
                                  setValue("service_practitioner", selected);
                                }}
                              />
                            )}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Group controlId="lab_tests">
                          <label className="form-label">Lab Tests</label>
                          <Controller
                            name="lab_tests"
                            control={control}
                            render={({ field }) => (
                              <MultiSelect
                                {...field}
                                isMulti
                                menuPlacement="auto"
                                closeMenuOnSelect={false}
                                isClearable
                                disabled={viewModal}
                                options={labTestList?.map((test) => ({
                                  label: `${test.lab_test_code.loinc_code} - ${test.lab_test_code.loinc_description}`,
                                  value: test.lab_test_code.loinc_code,
                                  fullData: test.lab_test_code,
                                }))}
                                placeholder="Select Lab Tests"
                                value={field.value?.map((item) => ({
                                  label: `${item.loinc_code} - ${item.loinc_description}`,
                                  value: item.loinc_code,
                                  fullData: item,
                                }))}
                                onChange={(selected) => {
                                  const newValue = selected.map(
                                    (option) => option.fullData
                                  );
                                  setValue("lab_tests", newValue);
                                }}
                                formatOptionLabel={({ label }) => (
                                  <div>{label}</div>
                                )}
                                getOptionValue={(option) => option.value}
                                components={{
                                  MultiValue: ({ data }) => (
                                    <div className="me-1">{data.label}</div>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </FalconComponentCard.Body>
                </FalconComponentCard>
              </Col>
              {(watch("service_type")?.servtype_code === "itm_medcde" ||
                watch("service_type")?.servtype_code === "itm_hrblcde") && (
                  <Col xs={12}>
                    <MedicalHistoryMedicine
                      watch={watch}
                      control={control}
                      setValue={setValue}
                    />
                  </Col>
                )}
            </Row>
            <ShowError errors={errors} />
          </Modal.Body>
          {!viewModal && (
            <Modal.Footer className="border-top pt-2">
              {selectedService?.id ? (
                <Button
                  variant="primary"
                  type="submit"
                  size="sm"
                  className="fs-11"
                >
                  <FontAwesomeIcon icon={faPen} className="me-2" />
                  Edit Service
                </Button>
              ) : (
                <Button
                  variant="primary"
                  type="submit"
                  size="sm"
                  className="fs-11"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add Services
                </Button>
              )}
            </Modal.Footer>
          )}
        </Form>
      </Modal>
      <WizardModal
        modal={showServiceCodeModal}
        setModal={setShowServiceCodeModal}
        title="Service Type Not Selected"
        description={
          "Please select Service Type first before choosing Service code / description"
        }
      />
      <TeethModalSelect modal={teethModal} setModal={setTeethModal} />
    </>
  );
};

export default MedicalHistoryModal;
